import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  A2PRegistrationInfoInterface,
  A2PRegistrationStatusInterface,
  BrandStatus,
  MessagingCampaignStatus,
  ValidationStatus,
} from '@vendasta/smsv2';
import { A2P_START_DATE, RegistrationStage } from './a2p-registration.constants';

export function hasRegistrationError(status: A2PRegistrationStatusInterface | undefined): boolean {
  if (!status) {
    return false;
  }

  const isBusinessProfileNonCompliant =
    status.secondaryBusinessProfile?.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT;
  const isTrustBundleNonCompliant = status.trustBundle?.status === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT;
  const isBrandFailed = status.brand?.status === BrandStatus.BRAND_STATUS_FAILED;
  const isCampaignFailed =
    status.messagingCampaign?.status === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_FAILED;

  return isBusinessProfileNonCompliant || isTrustBundleNonCompliant || isBrandFailed || isCampaignFailed;
}

export function isAwaitingStatusCreation(
  status: A2PRegistrationStatusInterface | undefined,
  info: A2PRegistrationInfoInterface | undefined,
): boolean {
  if (!status && info?.submitted) {
    return info.submitted > A2P_START_DATE;
  }

  if (status && hasRegistrationError(status) && status.updated && info?.submitted) {
    return info.submitted > status.updated;
  }

  return false;
}

export function getRegistrationStage(
  a2pRegistrationStatus: A2PRegistrationStatusInterface | undefined,
  a2pRegistrationInfo: A2PRegistrationInfoInterface | undefined,
): RegistrationStage {
  if (!a2pRegistrationInfo || !a2pRegistrationStatus) {
    return RegistrationStage.NotSubmitted;
  }
  const isSubmitted = !!a2pRegistrationInfo?.submitted && a2pRegistrationInfo.submitted > A2P_START_DATE;

  if (!isSubmitted) {
    return RegistrationStage.NotSubmitted;
  }

  if (isAwaitingStatusCreation(a2pRegistrationStatus, a2pRegistrationInfo)) {
    return RegistrationStage.ReviewingInfo;
  }

  const secondaryBusinessProfileStatus =
    a2pRegistrationStatus.secondaryBusinessProfile?.status ?? ValidationStatus.VALIDATION_STATUS_UNSPECIFIED;
  const trustBundleStatus = a2pRegistrationStatus.trustBundle?.status ?? ValidationStatus.VALIDATION_STATUS_UNSPECIFIED;
  const brandStatus = a2pRegistrationStatus.brand?.status ?? BrandStatus.BRAND_STATUS_UNSPECIFIED;
  const messagingCampaignStatus =
    a2pRegistrationStatus.messagingCampaign?.status ?? MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_UNSPECIFIED;

  if (
    secondaryBusinessProfileStatus === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT ||
    trustBundleStatus === ValidationStatus.VALIDATION_STATUS_NON_COMPLIANT ||
    brandStatus === BrandStatus.BRAND_STATUS_FAILED
  ) {
    return RegistrationStage.DataError;
  }

  if (messagingCampaignStatus === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_FAILED) {
    return RegistrationStage.CampaignError;
  }

  if (messagingCampaignStatus === MessagingCampaignStatus.MESSAGING_CAMPAIGN_STATUS_VERIFIED) {
    return RegistrationStage.RegistrationComplete;
  }

  return RegistrationStage.ReviewingInfo;
}

export function createGroupValueChangeValidator(
  groupOfFields: string[],
  initialValues: Map<string, string>,
  getAllControls: () => Record<string, FormControl>,
): ValidatorFn {
  const originalValuesMap = new Map<string, string>();
  groupOfFields.forEach((fieldName) => {
    const val = initialValues.get(fieldName);
    if (val !== undefined) {
      originalValuesMap.set(fieldName, val);
    }
  });

  return (): ValidationErrors | null => {
    const controls = getAllControls();
    for (const [fieldName, originalValue] of originalValuesMap.entries()) {
      const c = controls[fieldName];
      if (c !== undefined && c.value !== originalValue) {
        return null;
      }
    }
    return { groupValueError: true };
  };
}
